import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot } from 'lucide-react';
import '../styles/AiChatAssistant.css';

const MOCK_RESPONSES = {
  default: "I'm the Future Forbes AI assistant. I can help you find products, check our services, or apply for a career! How can I help?",
  hello: "Hello there! Welcome to Future Forbes. How can I assist you today?",
  hi: "Hi! How can I help you navigate our services?",
  product: "We have an amazing suite of products including FFHRMS, EasyXerox, and Planto. You can check them out on our Products page!",
  service: "Our services range from custom software development to advanced AI solutions. Feel free to visit the Services page for details.",
  career: "Looking for a job? We're always hiring talented individuals. Head over to our Careers page to apply!",
  contact: "You can reach us through the Contact Us page, or just leave your email here and we'll get back to you.",
};

const AiChatAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, text: "Hi! I'm your AI assistant. How can I help you today?", isBot: true }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen, isTyping]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMsg = { id: Date.now(), text: inputValue, isBot: false };
    setMessages(prev => [...prev, userMsg]);
    setInputValue("");
    setIsTyping(true);

    // Simulate AI response delay
    setTimeout(() => {
      const lowerInput = userMsg.text.toLowerCase();
      let responseText = MOCK_RESPONSES.default;

      if (lowerInput.includes('hello') || lowerInput.includes('hi')) responseText = MOCK_RESPONSES.hello;
      else if (lowerInput.includes('product')) responseText = MOCK_RESPONSES.product;
      else if (lowerInput.includes('service')) responseText = MOCK_RESPONSES.service;
      else if (lowerInput.includes('career') || lowerInput.includes('job')) responseText = MOCK_RESPONSES.career;
      else if (lowerInput.includes('contact')) responseText = MOCK_RESPONSES.contact;

      const botMsg = { id: Date.now() + 1, text: responseText, isBot: true };
      setMessages(prev => [...prev, botMsg]);
      setIsTyping(false);
    }, 1500);
  };

  return (
    <div className="ai-chat-wrapper">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="ai-chat-window"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div className="ai-chat-header">
              <div className="ai-chat-header-info">
                <div className="ai-bot-avatar">
                  <Bot size={20} />
                </div>
                <div>
                  <h4>Future Forbes AI</h4>
                  <span>Online</span>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="ai-close-btn">
                <X size={20} />
              </button>
            </div>

            <div className="ai-chat-messages">
              {messages.map((msg) => (
                <div key={msg.id} className={`ai-message-row ${msg.isBot ? 'bot' : 'user'}`}>
                  {msg.isBot && <div className="ai-message-avatar"><Bot size={14} /></div>}
                  <div className={`ai-message-bubble ${msg.isBot ? 'bot' : 'user'}`}>
                    {msg.text}
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="ai-message-row bot">
                  <div className="ai-message-avatar"><Bot size={14} /></div>
                  <div className="ai-message-bubble bot typing">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSendMessage} className="ai-chat-input-area">
              <input
                type="text"
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              <button type="submit" disabled={!inputValue.trim()}>
                <Send size={18} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        className="ai-chat-trigger"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={28} />}
      </motion.button>
    </div>
  );
};

export default AiChatAssistant;
